import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BlogService from "../../../api/services/BlogService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import Footer from "../../../customComponents/Footer";
import "./blog.css";

const formatBlogDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const authorInitial = (name) => {
  const t = String(name || "").trim();
  return t ? t.charAt(0).toUpperCase() : "A";
};

const RelThumbIcon = ({ index }) =>
  index % 2 === 0 ? (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden>
      <path d="M4 24 12 14l6 5 10-13" stroke="#d4a94e" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="4" cy="24" r="2.4" fill="#d4a94e" />
    </svg>
  ) : (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden>
      <rect x="6" y="6" width="20" height="20" rx="4" stroke="#d4a94e" strokeWidth="2.2" />
      <path d="M12 16.5l3 3 5.5-6" stroke="#d4a94e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

const BlogDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [blog, setBlog] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTocId, setActiveTocId] = useState("");
  const sidebarColRef = useRef(null);
  const asideRef = useRef(null);

  const qs = typeof window !== "undefined" ? window.location.search?.slice(1) || "" : "";
  const slug = id || searchParams.get("id") || (qs.includes("=") ? "" : qs) || "";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      setBlog(null);
      setNotFound(true);
      return undefined;
    }
    let cancelled = false;
    setNotFound(false);
    setBlog(null);
    (async () => {
      LoaderHelper.loaderStatus(true);
      try {
        const result = await BlogService.getBlogBySlug(slug);
        if (cancelled) return;
        if (result?.success && result?.data) {
          setBlog(result.data);
          setNotFound(false);
        } else {
          setBlog(null);
          setNotFound(true);
        }
      } catch {
        if (!cancelled) {
          setBlog(null);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) LoaderHelper.loaderStatus(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const toc = Array.isArray(blog?.toc) ? blog.toc : [];

  useEffect(() => {
    if (!toc.length) return undefined;
    setActiveTocId(toc[0]?.id || "");
    const ids = toc.map((item) => item.id).filter(Boolean);
    const els = ids.map((sid) => document.getElementById(sid)).filter(Boolean);
    if (!els.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActiveTocId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [toc, blog]);

  useEffect(() => {
    const col = sidebarColRef.current;
    const aside = asideRef.current;
    if (!col || !aside) return undefined;

    const desktopMq = window.matchMedia("(min-width: 768px)");

    const placeAside = () => {
      if (!desktopMq.matches) {
        aside.removeAttribute("style");
        return;
      }
      const r = col.getBoundingClientRect();
      aside.style.position = "fixed";
      aside.style.top = "96px";
      aside.style.left = `${Math.round(r.left)}px`;
      aside.style.width = `${Math.round(r.width)}px`;
      aside.style.maxHeight = "calc(100vh - 112px)";
      aside.style.overflowY = "auto";
      aside.style.zIndex = "4";
    };

    placeAside();
    const ro = new ResizeObserver(placeAside);
    ro.observe(col);
    window.addEventListener("resize", placeAside);
    desktopMq.addEventListener("change", placeAside);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", placeAside);
      desktopMq.removeEventListener("change", placeAside);
      aside.removeAttribute("style");
    };
  }, [blog]);

  const shareUrl =
    blog?.share_url || (typeof window !== "undefined" ? window.location.href : "");

  const handleCopy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).catch(() => { });
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      setActiveTocId(sectionId);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const categories = Array.isArray(blog?.categories) ? blog.categories : [];
  const related = Array.isArray(blog?.related) ? blog.related : [];
  const takeaways = Array.isArray(blog?.key_takeaways) ? blog.key_takeaways : [];

  if (notFound && !blog) {
    return (
      <div className="blog_detail_page">
        <Helmet>
          <title>Blog not found – AGCX</title>
        </Helmet>
        <div className="container blog_detail_container">
          <Link to="/blogs" className="blog_detail_back">
            <span className="blog_detail_back_arrow" aria-hidden="true">←</span>
            Back to Blog
          </Link>
          <p className="blog-empty">This blog is unavailable.</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog_detail_page">
        <Helmet>
          <title>Blog Details – AGCX</title>
        </Helmet>
      </div>
    );
  }

  return (
    <>
      <div className="blog_detail_page">
        <Helmet>
          <title>{blog.meta_title || `${blog.title} – AGCX Blog`}</title>
          <meta
            name="description"
            content={blog.meta_description || blog.subtitle || blog.excerpt || "AGCX Crypto & Trading Insights"}
          />
        </Helmet>

        <div className="container blog_detail_container">
          <div className="blog_detail_layout">
            <div className="blog_detail_main">
              <Link to="/blogs" className="blog_detail_back">
                <span className="blog_detail_back_arrow" aria-hidden="true">←</span>
                Back to Blog
              </Link>

              <div className="blog_detail_tags">
                {categories.map((cat) => (
                  <span
                    key={cat.slug || cat.name}
                    className="blog_tag_pill"
                    style={cat.color ? { background: cat.color, color: "#fff" } : undefined}
                  >
                    {cat.name}
                  </span>
                ))}
              </div>

              <h1 className="blog_detail_title">{blog.title}</h1>

              {blog.subtitle && <p className="blog_detail_subtitle">{blog.subtitle}</p>}

              {blog.banner_url && (
                <div className="blog_detail_hero_img_wrap">
                  <img
                    src={blog.banner_url}
                    alt={blog.title}
                    className="blog_detail_hero_img"
                  />
                </div>
              )}

              <div className="blog_detail_author_meta">
                <div className="author_avatar">
                  {blog.author_avatar_url ? (
                    <img src={blog.author_avatar_url} alt="" />
                  ) : (
                    authorInitial(blog.author_name)
                  )}
                </div>
                <div className="author_info">
                  <span className="author_name">{blog.author_name}</span>
                  <span className="meta_dot">•</span>
                  <span className="meta_time_date">
                    🕒 {blog.read_time_minutes} min read - {formatBlogDate(blog.published_at)}
                  </span>
                </div>
              </div>

              {takeaways.length > 0 && (
                <div className="blog_takeaways_card">
                  <h3 className="takeaways_title">Key Takeaway</h3>
                  <ol className="takeaways_list">
                    {takeaways.map((item, idx) => (
                      <li key={idx}>
                        <span className="list_num">{idx + 1}.</span>
                        <span className="list_text">{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {blog.content_html && (
                <div
                  className="blog-prose"
                  dangerouslySetInnerHTML={{ __html: blog.content_html }}
                />
              )}
            </div>

            <div className="blog_detail_sidebar" ref={sidebarColRef}>
              <aside className="blog_aside" ref={asideRef}>
                <section className="blog_aside_sec">
                  <div className="blog_aside_head">
                    <h3>Share this article</h3>
                  </div>
                  <button
                    type="button"
                    className={`blog_aside_copy${copied ? " is-copied" : ""}`}
                    onClick={handleCopy}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden>
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    <span>{copied ? "Copied" : "Copy link"}</span>
                  </button>
                </section>

                {toc.length > 0 && (
                  <section className="blog_aside_sec">
                    <div className="blog_aside_head">
                      <h3>On this page</h3>
                      {blog.read_time_minutes != null && (
                        <span className="blog_aside_meta">{blog.read_time_minutes} min read</span>
                      )}
                    </div>
                    <ul className="blog_aside_toc">
                      {toc.map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            className={activeTocId === item.id ? "is-active" : ""}
                            onClick={() => scrollToSection(item.id)}
                          >
                            {item.text}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {related.length > 0 && (
                  <section className="blog_aside_sec">
                    <div className="blog_aside_head">
                      <h3>You may also like</h3>
                    </div>
                    {related.map((item, idx) => (
                      <Link key={item.slug} to={`/blogs/${item.slug}`} className="blog_aside_rel">
                        <div className="blog_aside_rel_thumb">
                          {item.banner_url ? (
                            <img src={item.banner_url} alt="" />
                          ) : (
                            <RelThumbIcon index={idx} />
                          )}
                        </div>
                        <div className="blog_aside_rel_body">
                          {item.categories?.[0]?.name && (
                            <span className="blog_aside_rel_kicker">{item.categories[0].name}</span>
                          )}
                          <span className="blog_aside_rel_title">{item.title}</span>
                          {item.read_time_minutes != null && (
                            <span className="blog_aside_rel_meta">{item.read_time_minutes} min read</span>
                          )}
                        </div>
                      </Link>
                    ))}
                    <Link to="/blogs" className="blog_aside_viewall">
                      View all articles
                      <svg viewBox="0 0 24 24" aria-hidden>
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </Link>
                  </section>
                )}

                <div className="blog_aside_cta">
                  <div className="blog_aside_cta_inner">
                    <h4>Start trading on AGCX</h4>
                    <p>Spot, futures and earning products on a regulated Middle East exchange.</p>
                    <Link to="/signup" className="blog_aside_cta_btn">Create free account</Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        <div className={`blog_aside_toast${copied ? " is-show" : ""}`}>Link copied to clipboard</div>

      </div>
      <Footer />
    </>
  );
};

export default BlogDetails;
